import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Trophy, Target, Star, Download, List, LayoutDashboard, Pencil, Check, X, RefreshCw, Wifi, WifiOff, LogOut } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from './supabase';

const PLAYERS = ['Paul', 'Daniel'];

const emptyPlayerForm = () => ({
  handicap: '', gross: '', net: '', stableford: '',
  pars: '', birdies: '', eagles: '', holeInOnes: ''
});
const emptyRoundForm = () => ({
  course: '', date: new Date().toISOString().slice(0, 10),
  par: '', holes: '18', paul: emptyPlayerForm(), daniel: emptyPlayerForm()
});

// ─── Login Screen ─────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ic = 'w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError('Incorrect email or password.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="bg-emerald-700 text-white p-3 rounded-2xl">
            <Target size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Golf Tracker</h1>
            <p className="text-sm text-stone-500">Paul & Daniel</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h2 className="font-semibold text-stone-700 mb-5 text-center">Sign in to continue</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Email</label>
              <input
                type="email"
                className={ic}
                placeholder="you@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Password</label>
              <input
                type="password"
                className={ic}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-medium px-5 py-3 rounded-lg transition-colors"
            >
              {loading ? <RefreshCw size={18} className="animate-spin"/> : null}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PlayerFormSection({ playerKey, label, data, onChange }) {
  const ic = 'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600';
  const lc = 'block text-xs font-medium text-stone-600 mb-1';
  const fields = [
    ['handicap', 'Handicap', '0.1'], ['gross', 'Gross Score', '1'],
    ['net', 'Net Score', '1'], ['stableford', 'Stableford Pts', '1'],
    ['pars', 'Pars', '1'], ['birdies', 'Birdies', '1'],
    ['eagles', 'Eagles', '1'], ['holeInOnes', 'Hole in Ones', '1'],
  ];
  return (
    <div className="border border-stone-200 rounded-xl p-3">
      <h3 className="text-sm font-bold text-emerald-700 mb-3">{label}</h3>
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([field, fieldLabel, step]) => (
          <div key={field}>
            <label className={lc}>{fieldLabel}</label>
            <input type="number" step={step} className={ic} value={data[field]}
              onChange={e => onChange(playerKey, field, e.target.value)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function EditRoundForm({ round, onSave, onCancel }) {
  const ic = 'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600';
  const lc = 'block text-xs font-medium text-stone-600 mb-1';
  const [ed, setEd] = useState({
    course: round.course, date: round.date, par: round.par,
    holes: round.holes || '18', handicap: round.handicap,
    gross: round.gross, net: round.net_score, stableford: round.stableford,
    pars: round.pars || 0, birdies: round.birdies,
    eagles: round.eagles, holeInOnes: round.hole_in_ones,
  });
  const set = (f, v) => setEd(e => ({ ...e, [f]: v }));
  const fields = [
    ['handicap','Handicap'],['gross','Gross'],['net','Net'],
    ['stableford','Stableford'],['pars','Pars'],['birdies','Birdies'],
    ['eagles','Eagles'],['holeInOnes','Hole in Ones'],
  ];
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div className="col-span-2">
          <label className={lc}>Course</label>
          <input type="text" className={ic} value={ed.course} onChange={e => set('course', e.target.value)} />
        </div>
        <div>
          <label className={lc}>Date</label>
          <input type="date" className={ic} value={ed.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div>
          <label className={lc}>Par</label>
          <input type="number" className={ic} value={ed.par} onChange={e => set('par', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={lc}>Holes</label>
          <div className="flex rounded-lg border border-stone-300 overflow-hidden w-40">
            {['9','18'].map(h => (
              <button key={h} type="button" onClick={() => set('holes', h)}
                className={`flex-1 py-1.5 text-sm font-medium transition-colors ${ed.holes === h ? 'bg-emerald-700 text-white' : 'bg-white text-stone-600'}`}>
                {h}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {fields.map(([f, lbl]) => (
          <div key={f}>
            <label className={lc}>{lbl}</label>
            <input type="number" className={ic} value={ed[f]} onChange={e => set(f, e.target.value)} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(ed)}
          className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <Check size={15} /> Save
        </button>
        <button onClick={onCancel}
          className="flex items-center gap-1.5 bg-white border border-stone-300 text-stone-600 text-sm font-medium px-4 py-2 rounded-lg">
          <X size={15} /> Cancel
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, color }) {
  const textColor = color === 'good' ? 'text-emerald-700' : color === 'bad' ? 'text-red-600' : highlight ? 'text-emerald-800' : 'text-stone-800';
  return (
    <div className={`rounded-lg py-1.5 ${highlight ? 'bg-emerald-50' : 'bg-stone-50'}`}>
      <div className={`text-sm font-bold ${textColor}`}>{value}</div>
      <div className="text-[10px] text-stone-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function OverviewStat({ label, value, color }) {
  const textColor = color === 'good' ? 'text-emerald-700' : color === 'bad' ? 'text-red-600' : 'text-stone-900';
  return (
    <div className="bg-stone-50 rounded-xl py-3 px-2">
      <div className={`text-xl font-bold ${textColor}`}>{value}</div>
      <div className="text-[10px] text-stone-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [rounds, setRounds] = useState([]);
  const [form, setForm] = useState(emptyRoundForm());
  const [filter, setFilter] = useState('All');
  const [tab, setTab] = useState('log');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  // Auth state listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Online/offline
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  const loadRounds = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rounds')
        .select('*')
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setRounds(data || []);
      setError('');
    } catch (e) {
      setError('Could not load rounds. Check your Supabase credentials.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) loadRounds();
  }, [session, loadRounds]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleRoundChange = useCallback((field, value) => setForm(f => ({ ...f, [field]: value })), []);
  const handlePlayerChange = useCallback((playerKey, field, value) =>
    setForm(f => ({ ...f, [playerKey]: { ...f[playerKey], [field]: value } })), []);

  const addRound = async () => {
    if (!form.course) { setError('Please enter a course name.'); return; }
    setError('');
    setSaving(true);
    const newRows = [];
    PLAYERS.forEach(p => {
      const pKey = p.toLowerCase();
      const pd = form[pKey];
      if (pd.gross === '' && pd.net === '') return;
      newRows.push({
        player: p, course: form.course, date: form.date,
        par: Number(form.par) || 0, holes: form.holes,
        handicap: Number(pd.handicap) || 0,
        gross: Number(pd.gross) || 0,
        net_score: Number(pd.net) || 0,
        stableford: Number(pd.stableford) || 0,
        pars: Number(pd.pars) || 0,
        birdies: Number(pd.birdies) || 0,
        eagles: Number(pd.eagles) || 0,
        hole_in_ones: Number(pd.holeInOnes) || 0,
      });
    });
    if (newRows.length === 0) { setError('Enter scores for at least one player.'); setSaving(false); return; }
    try {
      const { error } = await supabase.from('rounds').insert(newRows);
      if (error) throw error;
      setForm(emptyRoundForm());
      await loadRounds();
    } catch (e) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRound = async (id) => {
    try {
      const { error } = await supabase.from('rounds').delete().eq('id', id);
      if (error) throw error;
      setRounds(r => r.filter(x => x.id !== id));
    } catch (e) { setError('Failed to delete.'); }
  };

  const saveEdit = async (id, ed) => {
    try {
      const { error } = await supabase.from('rounds').update({
        course: ed.course, date: ed.date,
        par: Number(ed.par) || 0, holes: ed.holes,
        handicap: Number(ed.handicap) || 0,
        gross: Number(ed.gross) || 0,
        net_score: Number(ed.net) || 0,
        stableford: Number(ed.stableford) || 0,
        pars: Number(ed.pars) || 0,
        birdies: Number(ed.birdies) || 0,
        eagles: Number(ed.eagles) || 0,
        hole_in_ones: Number(ed.holeInOnes) || 0,
      }).eq('id', id);
      if (error) throw error;
      setEditingId(null);
      await loadRounds();
    } catch (e) { setError('Failed to update.'); }
  };

  const filtered = filter === 'All' ? rounds : rounds.filter(r => r.player === filter);

  const vsPar = (r) => {
    if (!r.par || !r.net_score) return '-';
    const diff = r.net_score - r.par;
    return diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`;
  };
  const vsParNum = (r) => (!r.par || !r.net_score) ? 0 : r.net_score - r.par;

  const overviewFor = (player) => {
    const list = rounds.filter(r => r.player === player);
    return list.reduce((acc, r) => ({
      rounds: acc.rounds + 1,
      vsPar: acc.vsPar + vsParNum(r),
      stableford: acc.stableford + (r.stableford || 0),
      pars: acc.pars + (r.pars || 0),
      birdies: acc.birdies + (r.birdies || 0),
      eagles: acc.eagles + (r.eagles || 0),
      holeInOnes: acc.holeInOnes + (r.hole_in_ones || 0),
      holes: acc.holes + (Number(r.holes) || 18),
    }), { rounds: 0, vsPar: 0, stableford: 0, pars: 0, birdies: 0, eagles: 0, holeInOnes: 0, holes: 0 });
  };

  const exportXlsx = () => {
    const headers = ['Player','Date','Course','Holes','Par','Handicap','Gross','Net','vs Par','Stableford','Pars','Birdies','Eagles','Hole in Ones'];
    const rows = rounds.map(r => [
      r.player, r.date, r.course, r.holes, r.par, r.handicap,
      r.gross, r.net_score, vsPar(r), r.stableford, r.pars || 0,
      r.birdies, r.eagles, r.hole_in_ones,
    ]);
    const summaryRows = [];
    PLAYERS.forEach(player => {
      const t = overviewFor(player);
      if (t.rounds === 0) return;
      const vpl = t.vsPar === 0 ? 'E' : t.vsPar > 0 ? `+${t.vsPar}` : `${t.vsPar}`;
      summaryRows.push([]);
      summaryRows.push([`${player} TOTALS`, `${t.rounds} rounds`, `${t.holes} holes`, '', '', '', '', '', vpl, t.stableford, t.pars, t.birdies, t.eagles, t.holeInOnes]);
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows, ...summaryRows]);
    ws['!cols'] = [12,12,24,8,6,10,8,6,8,12,6,8,8,14].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Golf Rounds');
    XLSX.writeFile(wb, `golf-scores-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const ic = 'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600';
  const lc = 'block text-xs font-medium text-stone-600 mb-1';

  // Show nothing while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-emerald-700" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!session) {
    return <LoginScreen />;
  }

  return (
    <div className="min-h-screen bg-stone-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">

        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-700 text-white p-2.5 rounded-xl"><Target size={24} /></div>
            <div>
              <h1 className="text-xl font-bold text-stone-900">Golf Tracker</h1>
              <p className="text-sm text-stone-500">Paul & Daniel</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {online
              ? <span className="flex items-center gap-1 text-xs text-emerald-600"><Wifi size={14}/> Live</span>
              : <span className="flex items-center gap-1 text-xs text-red-500"><WifiOff size={14}/> Offline</span>}
            <button onClick={loadRounds} className="p-2 text-stone-400 hover:text-emerald-700 rounded-lg">
              <RefreshCw size={16} />
            </button>
            <button onClick={handleSignOut} className="p-2 text-stone-400 hover:text-red-600 rounded-lg" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-stone-200 p-1">
          {[['log', <List size={15}/>, 'Log Rounds'], ['overview', <LayoutDashboard size={15}/>, 'Overview']].map(([key, icon, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${tab === key ? 'bg-emerald-700 text-white' : 'text-stone-600'}`}>
              {icon} {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex justify-between items-center">
            {error}
            <button onClick={() => setError('')} className="ml-2"><X size={14}/></button>
          </div>
        )}

        {/* LOG TAB */}
        {tab === 'log' && (
          <>
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5 mb-5">
            <h2 className="font-semibold text-stone-700 mb-3 text-sm uppercase tracking-wide">Round Details</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="col-span-2">
                <label className={lc}>Course Name</label>
                <input type="text" placeholder="e.g. London Golf Club" className={ic} value={form.course}
                  onChange={e => handleRoundChange('course', e.target.value)} />
              </div>
              <div>
                <label className={lc}>Date</label>
                <input type="date" className={ic} value={form.date}
                  onChange={e => handleRoundChange('date', e.target.value)} />
              </div>
              <div>
                <label className={lc}>Par</label>
                <input type="number" className={ic} value={form.par}
                  onChange={e => handleRoundChange('par', e.target.value)} />
              </div>
              <div className="col-span-2 sm:col-span-4">
                <label className={lc}>Holes</label>
                <div className="flex rounded-lg border border-stone-300 overflow-hidden w-48">
                  {['9', '18'].map(h => (
                    <button key={h} type="button" onClick={() => handleRoundChange('holes', h)}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${form.holes === h ? 'bg-emerald-700 text-white' : 'bg-white text-stone-600'}`}>
                      {h} Holes
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <PlayerFormSection playerKey="paul" label="Paul" data={form.paul} onChange={handlePlayerChange} />
              <PlayerFormSection playerKey="daniel" label="Daniel" data={form.daniel} onChange={handlePlayerChange} />
            </div>
            <button onClick={addRound} disabled={saving}
              className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-medium px-5 py-2.5 rounded-lg transition-colors">
              {saving ? <RefreshCw size={18} className="animate-spin"/> : <Plus size={18}/>}
              {saving ? 'Saving...' : 'Add Round'}
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            {['All', ...PLAYERS].map(p => (
              <button key={p} onClick={() => setFilter(p)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === p ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-stone-600 border-stone-300'}`}>
                {p}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-stone-500 text-sm">
              <RefreshCw size={16} className="animate-spin"/> Loading rounds...
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-stone-500 text-sm">No rounds logged yet. Add one above.</p>
          ) : (
            <div className="space-y-3">
              {filtered.map(r => (
                <div key={r.id} className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
                  {editingId === r.id ? (
                    <EditRoundForm round={r} onSave={ed => saveEdit(r.id, ed)} onCancel={() => setEditingId(null)} />
                  ) : (
                    <>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">{r.player}</span>
                          <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">{r.holes || '18'} holes</span>
                        </div>
                        <h3 className="font-semibold text-stone-900">{r.course}</h3>
                        <p className="text-xs text-stone-500">{r.date}</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingId(r.id)} className="text-stone-400 hover:text-emerald-700 p-1"><Pencil size={15}/></button>
                        <button onClick={() => deleteRound(r.id)} className="text-stone-400 hover:text-red-600 p-1"><Trash2 size={15}/></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 text-center mb-2">
                      <Stat label="Par" value={r.par}/>
                      <Stat label="HCP" value={r.handicap}/>
                      <Stat label="Gross" value={r.gross} highlight/>
                      <Stat label="Net" value={r.net_score} highlight/>
                      <Stat label="Stableford" value={r.stableford}/>
                      <Stat label="Pars" value={r.pars || 0}/>
                      <Stat label="vs Par" value={vsPar(r)} color={vsParNum(r) < 0 ? 'good' : vsParNum(r) > 0 ? 'bad' : 'even'}/>
                    </div>
                    {(r.birdies > 0 || r.eagles > 0 || r.hole_in_ones > 0) && (
                      <div className="flex gap-3 text-xs text-stone-600 mt-2 border-t border-stone-100 pt-2">
                        {r.birdies > 0 && <span className="flex items-center gap-1"><Star size={12} className="text-amber-500"/> {r.birdies} Birdie{r.birdies > 1 ? 's' : ''}</span>}
                        {r.eagles > 0 && <span className="flex items-center gap-1"><Trophy size={12} className="text-amber-600"/> {r.eagles} Eagle{r.eagles > 1 ? 's' : ''}</span>}
                        {r.hole_in_ones > 0 && <span className="flex items-center gap-1 font-semibold text-emerald-700">🏌️ {r.hole_in_ones} Hole-in-One{r.hole_in_ones > 1 ? 's' : ''}</span>}
                      </div>
                    )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          </>
        )}

        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <button onClick={exportXlsx}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-medium px-5 py-2.5 rounded-lg transition-colors">
              <Download size={18}/> Export to Excel
            </button>
            {rounds.length === 0 ? (
              <p className="text-stone-500 text-sm">No rounds logged yet.</p>
            ) : PLAYERS.map(player => {
              const t = overviewFor(player);
              if (t.rounds === 0) return null;
              const vsParLabel = t.vsPar === 0 ? 'E' : t.vsPar > 0 ? `+${t.vsPar}` : `${t.vsPar}`;
              return (
                <div key={player} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-lg text-stone-900">{player}</h2>
                    <span className="text-xs text-stone-500">{t.rounds} round{t.rounds !== 1 ? 's' : ''} · {t.holes} holes</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <OverviewStat label="vs Par" value={vsParLabel} color={t.vsPar < 0 ? 'good' : t.vsPar > 0 ? 'bad' : 'even'}/>
                    <OverviewStat label="Stableford Pts" value={t.stableford}/>
                    <OverviewStat label="Pars" value={t.pars}/>
                    <OverviewStat label="Birdies" value={t.birdies}/>
                    <OverviewStat label="Eagles" value={t.eagles}/>
                    <OverviewStat label="Hole in Ones" value={t.holeInOnes}/>
                    <OverviewStat label="Total Holes" value={t.holes}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}